// Contact-us helper-text tooltip.
// `<contact-info-tooltip>` wraps a trigger button (the ⓘ info icon) and a `role="tooltip"`
// panel. Desktop (hover-capable) pointers show on hover AND toggle on click; touch/mobile
// toggle on click/tap. Closes on Esc, outside-click, or when another tooltip opens.
// Dependency-free ES module — does not extend the theme `Component` base (no shadow DOM /
// ref system needed here), mirroring `accordion-custom.js` which also extends HTMLElement.

/**
 * Module-level registry of currently-open tooltips, so opening one closes the others.
 * @type {Set<ContactInfoTooltip>}
 */
const openTooltips = new Set();

/** Matches pointers that can hover (desktop) — touch devices report `hover: none`. */
const hoverCapable = window.matchMedia('(hover: hover)');

class ContactInfoTooltip extends HTMLElement {
  /** @type {HTMLButtonElement | null} */
  #button = null;

  /** @type {HTMLElement | null} */
  #panel = null;

  /** Listeners bound only while the tooltip is open (document-level). */
  #openController = null;

  /** Listeners bound for the element's whole lifetime. */
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    const button = this.querySelector('button');
    const panel = this.querySelector('[role="tooltip"]');

    // Fail silently if the expected markup is missing (non-critical UI).
    if (!(button instanceof HTMLButtonElement) || !(panel instanceof HTMLElement)) return;

    this.#button = button;
    this.#panel = panel;

    button.addEventListener('click', this.#handleClick, { signal });

    // Hover only on hover-capable pointers, so touch taps don't double-trigger.
    if (hoverCapable.matches) {
      button.addEventListener('pointerenter', this.#handlePointerEnter, { signal });
      this.addEventListener('pointerleave', this.#handlePointerLeave, { signal });
    }
  }

  disconnectedCallback() {
    this.#close();
    this.#controller.abort();
    openTooltips.delete(this);
  }

  get #isOpen() {
    return this.#panel ? !this.#panel.hidden : false;
  }

  /** @param {PointerEvent} event */
  #handlePointerEnter = (event) => {
    // `pointerenter` can still fire for non-hover pointers on hybrid devices.
    if (event.pointerType === 'touch') return;
    this.#open();
  };

  /** @param {PointerEvent} event */
  #handlePointerLeave = (event) => {
    if (event.pointerType === 'touch') return;
    this.#close();
  };

  #handleClick = () => {
    this.#isOpen ? this.#close() : this.#open();
  };

  #open() {
    if (this.#isOpen || !this.#button || !this.#panel) return;

    // Close any other open tooltip first (only one visible at a time).
    for (const other of openTooltips) {
      if (other !== this) other.#close();
    }

    this.#panel.hidden = false;
    this.#button.setAttribute('aria-expanded', 'true');
    openTooltips.add(this);

    this.#openController = new AbortController();
    const { signal } = this.#openController;
    document.addEventListener('keydown', this.#handleKeyDown, { signal });
    // pointerdown (not click) so the outside-click closes before any focus shift.
    document.addEventListener('pointerdown', this.#handleOutsidePointer, { signal });
  }

  #close() {
    if (!this.#panel || !this.#button) return;

    this.#panel.hidden = true;
    this.#button.setAttribute('aria-expanded', 'false');
    openTooltips.delete(this);

    this.#openController?.abort();
    this.#openController = null;
  }

  /** @param {KeyboardEvent} event */
  #handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#close();
      this.#button?.focus();
    }
  };

  /** @param {PointerEvent} event */
  #handleOutsidePointer = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.#close();
  };
}

if (!customElements.get('contact-info-tooltip')) {
  customElements.define('contact-info-tooltip', ContactInfoTooltip);
}
